import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Box, Flex, Pagination } from '@legion-ui/core';
import Button from '../../components/Button';
import RecipientTable from '../../components/RecipientTable';
import { useEmailBlastById, useDeleteEmailBlast } from '@/hooks/useEmailBlastQuery';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FaTrash } from 'react-icons/fa';

const ViewEmailDraft: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);

  // Fetch email blast by ID
  const { data: emailDraft, isLoading } = useEmailBlastById(id!);
  const deleteEmailBlast = useDeleteEmailBlast();

  // Pagination logic
  const totalRecipients = emailDraft?.recipients?.length || 0;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRecipients = useMemo(() => {
    if (!emailDraft?.recipients) return [];
    return emailDraft.recipients.slice(startIndex, endIndex).map((recipient, index) => {
      return {
        id: `${startIndex + index + 1}`,
        username: recipient.username || '',
        email: recipient.email || '',
        fullName: recipient.fullname || '',
        status: recipient.status || 'unsent',
      };
    });
  }, [emailDraft?.recipients, startIndex, endIndex]);

  const handleBack = () => {
    navigate('/email-blast');
  };

  const handleEdit = () => {
    navigate(`/email-blast/edit/${id}`);
  };

  const handleDelete = async () => {
    if (!id) return;
    
    if (window.confirm('Are you sure you want to delete this draft?')) {
      try {
        await deleteEmailBlast.mutateAsync(id);
        // Navigate back to email blast list after successful deletion
        navigate('/email-blast', { replace: true });
      } catch (error: any) {
        // Show error message
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete email blast';
        alert(errorMessage);
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <Container className="py-8 px-6">
        <Box className="mb-6">
          <Skeleton height={32} width={200} />
        </Box>
        <Box className="mb-8 bg-white border border-gray-200 rounded-lg p-6">
          <Skeleton count={5} />
        </Box>
      </Container>
    );
  }

  if (!emailDraft) {
    return (
      <Container className="py-8 px-6">
        <Box className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Email not found</h1>
        </Box>
        <Button onClick={handleBack} variant="outline">
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-8 px-6">
      {/* Header */}
      <Box className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Draft Bulk Email</h1>
      </Box>

      {/* Compose Email Section */}
      <Box className="mb-8 bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Compose Email</h2>
          {emailDraft?.status === 'draft' && (
            <div className="flex gap-2">
              <Button
                onClick={handleEdit}
                variant="solid"
                color="primary"
                className="px-4 py-2"
              >
                Edit
              </Button>
              <Button
                onClick={handleDelete}
                variant="solid"
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
              >
                <FaTrash className="mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>
        
        {/* Subject */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
            {emailDraft.subject}
          </div>
        </div>

        {/* Body Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Body Email
          </label>
          <div 
            className="email-content w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 min-h-[300px]"
            dangerouslySetInnerHTML={{ __html: emailDraft.content }}
          />
        </div>
      </Box>

      {/* Recipient Section */}
      <Box className="mb-8 bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Recipient</h2>
        
        {/* Recipients Table with Status */}
        <RecipientTable recipients={paginatedRecipients} showStatus={true} />

        {/* Pagination */}
        {totalRecipients > 0 && (
          <Box mt="1rem">
            <Flex alignX="flex-end">
              <Pagination
                key={`pagination-${currentPage}-${pageSize}`}
                currentPage={currentPage}
                totalPages={totalRecipients}
                onClickPage={handlePageChange}
              />
            </Flex>
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <Button
          onClick={handleBack}
          variant="outline"
          className="px-6 py-2"
        >
          Back
        </Button>
      </div>
    </Container>
  );
};

export default ViewEmailDraft;
